import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, Col, ControlLabel, Form, FormControl, FormGroup, Panel, Checkbox } from 'react-bootstrap';
import * as ApiTypes from '../../models/ApiTypes';
import { bind } from '../../utils/decorators';
import Callout from '../site/Callout';
import AuftragAdresseErfassen from './adressen/AuftragAdresseErfassen';
import LadeFortschritt from '../site/LadeFortschritt';
import AuftragAnlegen from './AuftragAnlegen';

/** Formular zum Anlegen eines neuen Auftrags für die Kalibrierung von Bremsprüfständen (BPS)  */
@observer
export default class AuftragAnlegenBPS extends AuftragAnlegen {

  readonly kategorie = 'bps';

  @bind async handleAuftragAnlegenSubmit(event: React.FormEvent<FormControl>) {
    event.preventDefault();

    let daten: ({}) = {
      'auftragname': `BPS ${this.auftraggeber.name}`,
      'pinummer': this.verantwortlicher,
      'referenzdatum': this.referenzdatum,
      'lizenznehmer': this.lizenznehmer,
      'kategorie': this.kategorie,
      'bemerkung': this.bemerkung,
      'abweichenderuntersuchungsort': !this.untersuchungsortGleichAuftraggeber,
      'auftraggeber': {
        'name1': this.auftraggeber.name,
        'name2': this.auftraggeber.zusatz,
        'strasse': this.auftraggeber.strasse,
        'plz': this.auftraggeber.plz,
        'ort': this.auftraggeber.ort,
        'tel': this.auftraggeber.tel1,
        'email': this.auftraggeber.email,
        'email2': this.auftraggeber.email2,
        'adressid': this.auftraggeber.adressid
      },
      'beauftragter': {
        'name1': this.untersuchungsort.name,
        'name2': this.untersuchungsort.zusatz,
        'strasse': this.untersuchungsort.strasse,
        'plz': this.untersuchungsort.plz,
        'ort': this.untersuchungsort.ort,
        'tel': this.untersuchungsort.tel1,
        'email': this.untersuchungsort.email,
        'adressid': this.untersuchungsort.adressid
      }
    };

    const auftragResponse = await this.apiStore.auftragAnlegen(daten);

    if (auftragResponse.ok) {
      const anr = auftragResponse.json.success as string;
      this.uiStore.navigateTo(`/auftrag/${anr}`);
    }
  }

  async componentDidMount() {
    this.setAuftragskopfGeladen(false);
    this.setReferenzDatum();
    await this.uiStore.loadUserProfile();
    const userProfile = this.uiStore.userProfile;
    if (userProfile && userProfile.projektleiter) {
      await this.uiStore.loadPersonallisteKategorie(this.kategorie);
    }
    await this.uiStore.loadFreigaben();
    if (this.uiStore.freigaben) {
      if (this.uiStore.freigaben.length > 0) {
        this.setLizenznehmer(this.uiStore.freigaben[0].lizenznehmer);
      }
    }
    this.setAuftragskopfGeladen(true);
  }

  voraussetzungenErfuellt() {
    let result = (this.referenzdatum.trim() !== '') && (this.lizenznehmer) && (this.auftraggeber.name.trim() !== '');
    // Projektleiter dürfen den Auftrag anderen Personen zuweisen, in diesen Fällen muss zum speichern eine Person gewählt sein
    if (this.uiStore.userProfile && this.uiStore.userProfile.projektleiter) {
      result = result && (this.verantwortlicher !== '');
    }
    // Untersuchungsort nur validieren, wenn dieser vom Auftraggeber abweicht
    if (!this.untersuchungsortGleichAuftraggeber) {
      result = result && (this.untersuchungsort.name.trim() !== '');
    }
    return result;
  }

  render() {
    if (!this.auftragskopfGeladen) {
      return <LadeFortschritt/>;
    }

    return (
      <Form horizontal={true} onSubmit={this.handleAuftragAnlegenSubmit}>

        <h3>Neuen Auftrag für Kalibrierung Bremsprüfstand anlegen</h3>

        <Panel header="Auftragdetails">
          <FormGroup validationState={this.lizenznehmer === '' ? 'error' : undefined} controlId="Anlegen für">
            <Col componentClass={ControlLabel} sm={3}>Anlegen für</Col>
            <Col sm={9}>
              <FormControl componentClass="select" placeholder="select" value={this.lizenznehmer} onChange={this.handleLizenznehmerChanged}>
                {this.uiStore.freigaben &&
                  this.uiStore.freigaben.map((freigabe) =>
                    <option key={freigabe.lizenznehmer} value={freigabe.lizenznehmer}>{freigabe.name} ({freigabe.lizenznehmer})</option>
                  )
                }
              </FormControl>
            </Col>
          </FormGroup>
          <FormGroup validationState={this.referenzdatum === '' ? 'error' : undefined} controlId="Referenzdatum">
            <Col componentClass={ControlLabel} sm={3}>Referenzdatum</Col>
            <Col sm={9}>
              <FormControl type="date" label="Referenzdatum" value={this.referenzdatum} onChange={this.handleDatumChanged}
              />
            </Col>
          </FormGroup>
          {this.uiStore.userProfile && this.uiStore.userProfile.projektleiter &&
            (<FormGroup validationState={this.verantwortlicher === '' ? 'error' : undefined} controlId="Zuweisung">
              <Col componentClass={ControlLabel} sm={3}>Zuweisung</Col>
              <Col sm={9}>
                <FormControl componentClass="select" placeholder="select" value={this.verantwortlicher} onChange={this.handlePiChanged}>
                  <option value="">Bitte zuständige Person wählen...</option>
                  {this.uiStore.personalliste &&
                    this.uiStore.personalliste.map((personal) =>
                      <option key={personal.pi} value={personal.pi}>{personal.vorname} {personal.name} ({personal.pi})</option>
                    )
                  }
                </FormControl>
              </Col>
            </FormGroup>)
          }
        </Panel>

        {/* Eingabe für Auftraggeber */}
        <AuftragAdresseErfassen caption="Auftraggeber" adresse={this.auftraggeber} onUpdateAdresse={this.setAuftraggeber} adressArt={ApiTypes.AdressArt.Auftraggeber}/>

        {/* Eingabe für Untersuchungsort */}
        <Callout bsStyle="info">
          <Checkbox inline={true} id="checkboxSeparateOrtsEingabe" onChange={this.handleUntersuchungsortGleichAuftraggeberChange} defaultChecked={this.untersuchungsortGleichAuftraggeber}>Untersuchungssort entspricht Auftraggeber</Checkbox>
        </Callout>
        {!this.untersuchungsortGleichAuftraggeber &&
          <AuftragAdresseErfassen caption="Ort der Kalibrierung" adresse={this.untersuchungsort} onUpdateAdresse={this.setUntersuchungsort} adressArt={ApiTypes.AdressArt.Untersuchungsstelle}/>
        }

        {/* Eingabebereich Zusatz */}

        <Panel header="Bemerkung">
          <FormControl componentClass="textarea" type="text" label="Bemerkung" placeholder="Optional" value={this.bemerkung} onChange={this.handleBemerkungChanged}/>
        </Panel>

        {/* Workflow */}

        {!this.voraussetzungenErfuellt() && (
          <Callout bsStyle="danger">
            <p>Es wurden noch nicht alle erforderlichen Felder ausgefüllt!</p>
          </Callout>
        )}

        <div className="text-right">
          <Button onClick={() => this.uiStore.navigateBack()}>Abbrechen</Button>
          <Button bsStyle="primary" type="submit" disabled={!this.voraussetzungenErfuellt()}>Auftrag anlegen</Button>
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/AuftragAnlegenBPS.tsx